import { ExtractComponentProp } from '@approvalmax/types';
import { Flex, Progress, Radio, Spacing, Table, Text } from '@approvalmax/ui/src/components';
import { isEnumValue } from '@approvalmax/utils/src/helpers/misc/isEnumValue';
import { FC, memo, useCallback } from 'react';

import { useOffboardingContext } from '../../OffboardingContext';
import { useGetInvalidItems } from '../../OffboardUserPopupContent.hooks';
import { RequestReplacementMode } from '../../OffboardUserPopupContent.types';
import ReplacementCell from '../ReplacementCell/ReplacementCell';
import { ApprovalWorkflowsColumns } from './Requests.constants';
import { useRequestsData } from './Requests.hooks';
import { messages } from './Requests.messages';
import { RequestsProps } from './Requests.types';

export const Requests: FC<RequestsProps> = memo((props) => {
    const { companyId, isLoading, requests, displayName, quantityRequests, isWorkflowManager } = props;

    const { requestReplacementMode, setRequestReplacementMode } = useOffboardingContext();

    const data = useRequestsData(companyId, requests);

    const onChangeInviteWhenAdd = useCallback<ExtractComponentProp<typeof Radio.Group, 'onChange'>>(
        (requestReplacementMode) => {
            if (isEnumValue(RequestReplacementMode, requestReplacementMode)) {
                setRequestReplacementMode(requestReplacementMode);
            }
        },
        [setRequestReplacementMode]
    );

    const invalidItems = useGetInvalidItems(data);

    const hasData = data.length || (isWorkflowManager && typeof quantityRequests === 'number' && quantityRequests > 0);

    return hasData ? (
        <Flex direction='column'>
            <Flex>
                <Text font='body' as='span'>
                    {messages.title({ displayName })}
                </Text>

                <Radio.Group
                    name='requestReplacementModeRadio'
                    size='medium'
                    value={requestReplacementMode}
                    onChange={onChangeInviteWhenAdd}
                >
                    <Radio value={RequestReplacementMode.OneForAll}>
                        {isWorkflowManager
                            ? messages.countOpenRequests({
                                  count: quantityRequests,
                              })
                            : messages.allOpenRequests}
                    </Radio>

                    {!isWorkflowManager && (
                        <Radio value={RequestReplacementMode.Each}>{messages.eachOpenRequest}</Radio>
                    )}
                </Radio.Group>
            </Flex>

            {requestReplacementMode === RequestReplacementMode.OneForAll && !isLoading && (
                <Flex container wrap={false}>
                    <Flex size={8}>
                        <ReplacementCell invalid={invalidItems.length > 0} />
                    </Flex>

                    <Spacing height={40} />
                </Flex>
            )}

            {requestReplacementMode === RequestReplacementMode.Each && !isLoading && (
                <Table
                    headerColor='white100'
                    invalidItems={invalidItems}
                    data={isLoading ? [] : data}
                    columns={ApprovalWorkflowsColumns}
                />
            )}

            {isLoading && (
                <Flex justifyContent='center'>
                    <Progress shape='circle' />
                </Flex>
            )}
        </Flex>
    ) : (
        <>
            {isLoading && (
                <Flex justifyContent='center'>
                    <Progress shape='circle' />
                </Flex>
            )}

            <Text font='body' textAlign='center' color='midnight60'>
                {messages.noData}
            </Text>
        </>
    );
});

Requests.displayName = 'Requests';
